import { TwoColumnLayout } from 'layouts';
import type { EntityOptions, OrganizationDetail as OrganizationDetailType } from 'models';
import { hasMembershipInOrganizationProfile, usePermissions } from 'permissions';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { CommunitiesSlider } from 'features/communities/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { GivingsSlider } from 'features/givings/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { ProgramsSlider } from 'features/programs/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { VideosSlider } from 'features/videos/components';
import type { OrganizationEntityType } from '../models';
import { OrganizationAdminsCard } from './organization-admins-card';
import { OrganizationDetailDescription } from './organization-detail-description';
import { OrganizationDetailJoinButton } from './organization-detail-join-button';
import { OrganizationDetailsCard } from './organization-details-card';
import { OrganizationHierarchyCard } from './organization-hierarchy-card';

type P = {
  getPathToEntityDetail: (id: string, options: EntityOptions) => string;
  navigateToOrganizationEntity: (id: string, scope: OrganizationEntityType) => void;
  organization: OrganizationDetailType;
};

const OrganizationDetail = ({ getPathToEntityDetail, navigateToOrganizationEntity, organization }: P) => {
  const { railsUserData: viewer } = useUser();
  const isJoined = hasMembershipInOrganizationProfile({ organizationId: organization.id, viewer });

  const { canViewVideos } = usePermissions();

  return (
    <TwoColumnLayout
      left={
        <>
          {isJoined ? null : <OrganizationDetailJoinButton organizationId={organization.id} />}
          <OrganizationDetailDescription organization={organization} />
          <CommunitiesSlider
            colSpan={10}
            controls={{
              hasTitleIcon: true,
              seeAll: {
                onClick() {
                  navigateToOrganizationEntity(organization.id, 'communities');
                },
              },
              title: 'Communities',
              titleLevel: 4,
            }}
            getPathToCommunityDetail={(id) => getPathToEntityDetail(id, { entityType: 'community' })}
            query={{ filter: { featured: undefined, organizationId: organization.id } }}
          />
          <GivingsSlider
            colSpan={10}
            controls={{
              hasTitleIcon: true,
              seeAll: {
                onClick() {
                  navigateToOrganizationEntity(organization.id, 'givings');
                },
              },
              title: 'Giving',
              titleLevel: 4,
            }}
            getPathToGivingDetail={(id) => getPathToEntityDetail(id, { entityType: 'giving' })}
            query={{ filter: { featured: undefined, organizationId: organization.id } }}
          />
          <ProgramsSlider
            colSpan={10}
            controls={{
              hasTitleIcon: true,
              seeAll: {
                onClick() {
                  navigateToOrganizationEntity(organization.id, 'programs');
                },
              },
              title: 'Programs',
              titleLevel: 4,
            }}
            getPathToProgramDetail={(id) => getPathToEntityDetail(id, { entityType: 'program' })}
            query={{ filter: { featured: undefined, organizationId: organization.id } }}
          />
          {canViewVideos({ viewer }) ? (
            <VideosSlider
              colSpan={10}
              controls={{
                hasTitleIcon: true,
                seeAll: {
                  onClick() {
                    navigateToOrganizationEntity(organization.id, 'videos');
                  },
                },
                title: 'Videos',
                titleLevel: 4,
              }}
              getPathToVideoDetail={(id) => getPathToEntityDetail(id, { entityType: 'video' })}
              query={{ filter: { featured: undefined, organizationId: organization.id } }}
            />
          ) : null}
        </>
      }
      right={
        <>
          <OrganizationAdminsCard organizationId={organization.id} />
          <OrganizationHierarchyCard
            getPathToGroupDetail={(id) => getPathToEntityDetail(id, { entityType: 'group' })}
            organization={organization}
          />
          <OrganizationDetailsCard privacy={organization.privacy} />
        </>
      }
    />
  );
};

export default OrganizationDetail;
